// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Agents in Settings → Connections. The card (design/konvo-agents/c-card.png) shows how
// many machines are connected and opens either the connect dialog or the machines manager
// (c-machines.png): each machine's online state, platform, bridge and tool versions, counts and
// the limits its owner set on it (read-only here), with rename and disconnect; its Health and Agent
// accounts (AgentHealth.tsx, AgentAccountsSettings.tsx). Above them, what Enter does while an agent
// works (AgentFollowUpSettings.tsx).

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {MachineAccountsSection} from '@app/features/konvo_agents/components/AgentAccountsSettings';
import {useNow} from '@app/features/konvo_agents/components/AgentBits';
import {MachineGlyph, OnlineDot, ToolChip} from '@app/features/konvo_agents/components/AgentBrand';
import {AgentMachineBrowser} from '@app/features/konvo_agents/components/AgentBrowserSettings';
import {AgentFollowUpSettings} from '@app/features/konvo_agents/components/AgentFollowUpSettings';
import {MachineHealthSection} from '@app/features/konvo_agents/components/AgentHealth';
import {AgentMergeRightsSettings} from '@app/features/konvo_agents/components/AgentMergeRightsSettings';
import {AgentMachineSharing} from '@app/features/konvo_agents/components/AgentShareSettings';
import styles from '@app/features/konvo_agents/components/AgentsConnection.module.css';
import {openAgentsConnectModal} from '@app/features/konvo_agents/components/AgentsConnectModal';
import {agentErrorMessage, mayDo, offlineSince} from '@app/features/konvo_agents/components/AgentsCopy';
import {
	AgentMachineTerminals,
	showsTerminalsSetting,
} from '@app/features/konvo_agents/components/AgentTerminalsSettings';
import {canBrowse} from '@app/features/konvo_agents/state/AgentBrowser';
import Agents from '@app/features/konvo_agents/state/Agents';
import AgentsAccess from '@app/features/konvo_agents/state/AgentsAccess';
import {platformName, sharedProjects} from '@app/features/konvo_agents/state/AgentsModel';
import {KonvoConnectorCard} from '@app/features/konvo_connections/components/KonvoConnectorCard';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {AgentMachineView} from '@fluxer/schema/src/domains/konvo/Agents';
import {plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconArrowLeft, IconPencil, IconPlugConnectedX, IconPlus, IconTerminal2} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

/** Loads the overview while Connections is open (and on focus). */
export function useAgentsOverview(enabled: boolean): void {
	useEffect(() => {
		if (!enabled) return;
		void Agents.loadOverview();
		const onFocus = () => void Agents.loadOverview();
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, [enabled]);
}

/** Whether Connections should show Agents at all (the per-person switch). */
export function agentsConnectionVisible(): boolean {
	return AgentsAccess.enabled && Agents.enabled !== false;
}

export const AgentsConnectionCard = observer(({onManage}: {onManage: () => void}) => {
	const {t} = useLingui();
	useAgentsOverview(true);
	const machines = Agents.machines;
	const connected = machines.length > 0;
	const online = Agents.onlineCount;
	const toolNames = [...new Set(machines.flatMap((machine) => machine.tools.map((tool) => tool.name)))];
	const anySend = machines.some((machine) => machine.limits.send);
	const account = connected
		? `${plural(machines.length, {one: '# machine', other: '# machines'})} · ${t`${online} online`}`
		: null;
	return (
		<KonvoConnectorCard
			logo={<IconTerminal2 size={24} stroke={1.6} />}
			name={t`Agents`}
			description={t`Your coding agents' chats in Konvo. Konvo can send them prompts when you confirm.`}
			features={[
				...(toolNames.length > 0 ? toolNames : ['T3 Code']),
				t`Read chats`,
				...(anySend || !connected ? [t`Send prompts`] : []),
			]}
			connected={connected}
			account={account}
			actionLabel={connected ? t`Manage` : t`Connect`}
			actionVariant={connected ? 'secondary' : 'primary'}
			onAction={() => (connected ? onManage() : openAgentsConnectModal())}
			secondaryActionLabel={connected ? t`Add machine` : undefined}
			onSecondaryAction={connected ? openAgentsConnectModal : undefined}
			data-flx="konvo-agents.connection-card"
		/>
	);
});

const MachineCard = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const now = useNow(60_000);
	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(machine.name);
	const [saving, setSaving] = useState(false);
	const input = useRef<HTMLInputElement | null>(null);
	useEffect(() => {
		if (editing) input.current?.select();
	}, [editing]);
	const list = Agents.list(machine.id);
	const threadCount = list?.threads?.length ?? null;
	const shared = sharedProjects(machine.limits, machine.projects);
	const projectCount = machine.limits.projects === 'all' ? machine.projects.length : machine.limits.projects.length;

	const save = async () => {
		const next = name.trim();
		if (!next || next === machine.name) {
			setEditing(false);
			setName(machine.name);
			return;
		}
		setSaving(true);
		try {
			await Agents.rename(machine.id, next);
			setEditing(false);
		} catch (error) {
			ToastCommands.error(agentErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};

	const disconnect = () => {
		const machineName = machine.name;
		ModalCommands.push(
			ModalCommands.modal(() => (
				<ConfirmModal
					title={t`Disconnect ${machineName}?`}
					description={t`Konvo stops seeing this machine's agent chats straight away. The bridge on it stops at its next check. To connect it again, add it as a new machine.`}
					primaryText={t`Disconnect`}
					primaryVariant="danger"
					onPrimary={async () => {
						try {
							await Agents.disconnect(machine.id);
							ToastCommands.success(t`${machineName} disconnected.`);
						} catch (error) {
							ToastCommands.error(agentErrorMessage(i18n, error));
						}
					}}
				/>
			)),
		);
	};

	return (
		<section className={clsx(styles.machine, !machine.online && styles.machineOffline)} data-flx="konvo-agents.machine">
			<div className={styles.machineTop}>
				<span className={styles.glyph}>
					<MachineGlyph platform={machine.platform} size={22} />
				</span>
				<div className={styles.machineHeading}>
					{editing ? (
						<input
							ref={input}
							className={styles.nameInput}
							value={name}
							maxLength={80}
							disabled={saving}
							aria-label={t`Machine name`}
							onChange={(event) => setName(event.target.value)}
							onBlur={() => void save()}
							onKeyDown={(event) => {
								if (event.key === 'Enter') void save();
								if (event.key === 'Escape') {
									setName(machine.name);
									setEditing(false);
								}
							}}
							data-flx="konvo-agents.machine.name-input"
						/>
					) : (
						<h3 className={styles.machineName}>{machine.name}</h3>
					)}
					<p className={styles.machineMeta}>
						<OnlineDot online={machine.online} />
						<span>{machine.online ? t`Online` : offlineSince(i18n, machine.lastSeenAt, now)}</span>
						{platformName(machine.platform) && <span>· {platformName(machine.platform)}</span>}
						<span>· konvo-agents {machine.bridgeVersion}</span>
					</p>
				</div>
				<div className={styles.machineActions}>
					<button
						type="button"
						className={styles.round}
						onClick={() => {
							setName(machine.name);
							setEditing(true);
						}}
						aria-label={t`Rename ${machine.name}`}
						title={t`Rename`}
						data-flx="konvo-agents.machine.rename"
					>
						<IconPencil size={17} stroke={1.7} />
					</button>
					<button
						type="button"
						className={styles.round}
						onClick={disconnect}
						aria-label={t`Disconnect ${machine.name}`}
						title={t`Disconnect`}
						data-flx="konvo-agents.machine.disconnect"
					>
						<IconPlugConnectedX size={17} stroke={1.7} />
					</button>
				</div>
			</div>
			<div className={styles.tools}>
				{machine.tools.map((tool) => (
					<ToolChip key={tool.tool} tool={tool} />
				))}
				{machine.online && (
					<span className={styles.counts}>
						{plural(projectCount, {one: '# project', other: '# projects'})}
						{threadCount != null ? ` · ${plural(threadCount, {one: '# thread', other: '# threads'})}` : ''}
					</span>
				)}
			</div>
			{/* Health (checks and activity) and the Claude / Codex accounts (docs/konvo-agents/HEALTH.md). */}
			<MachineHealthSection machine={machine} />
			<MachineAccountsSection machine={machine} />
			<dl className={styles.limits}>
				<dt>
					<Trans>Konvo may</Trans>
				</dt>
				<dd>{mayDo(i18n, machine.limits)}</dd>
				<dt>
					<Trans>Projects shared</Trans>
				</dt>
				<dd>
					{shared.kind === 'all' ? (
						<Trans>All projects</Trans>
					) : shared.kind === 'none' ? (
						<Trans>None yet</Trans>
					) : (
						<>
							{shared.names.join(', ')}
							{shared.more > 0 && <span className={styles.more}> {t`+${shared.more} more`}</span>}
						</>
					)}
				</dd>
			</dl>
			{/* Team threads: how far this machine lets its threads be shared, and what is shared now. */}
			<AgentMachineSharing machine={machine} />
			{/* Terminals from Konvo: the owner's own terminals, live and typed into (on by default). */}
			{showsTerminalsSetting(machine) && <AgentMachineTerminals machine={machine} />}
			{/* The Agents browser: what it may reach through this machine, and who opened what. */}
			{machine.limits.send && (canBrowse(machine) || machine.limits.browserCap !== undefined) && (
				<AgentMachineBrowser machine={machine} />
			)}
		</section>
	);
});

export const AgentsMachinesManager = observer(({onBack}: {onBack: () => void}) => {
	const {t} = useLingui();
	useAgentsOverview(true);
	const onlineIds = Agents.machines
		.filter((machine) => machine.online)
		.map((machine) => machine.id)
		.join(',');
	useEffect(() => {
		// Thread counts for the online machines.
		for (const id of onlineIds.split(',').filter(Boolean)) if (!Agents.list(id)) void Agents.loadThreads(id);
	}, [onlineIds]);
	return (
		<section className={styles.manager} aria-label={t`Agents`} data-flx="konvo-agents.manager">
			<button type="button" className={styles.back} onClick={onBack} data-flx="konvo-agents.manager.back">
				<IconArrowLeft size={16} stroke={1.8} />
				<Trans>Connections</Trans>
			</button>
			<div className={styles.managerHead}>
				<div className={styles.managerTitles}>
					<h2 className={styles.managerTitle}>
						<Trans>Agents</Trans>
					</h2>
					<p className={styles.managerLead}>
						<Trans>Machines that let Konvo see your agent chats. Choose here what Konvo may do on each one.</Trans>
					</p>
				</div>
				<Button
					small
					fitContent
					leftIcon={<IconPlus size={16} stroke={2} />}
					onClick={openAgentsConnectModal}
					data-flx="konvo-agents.manager.add"
				>
					<Trans>Add machine</Trans>
				</Button>
			</div>
			{/* What Enter does with a message while an agent works: steer (default) or queue. */}
			{Agents.machines.length > 0 && <AgentFollowUpSettings />}
			{Agents.machines.length === 0 ? (
				<p className={styles.empty}>
					{!Agents.loaded ? (
						<Trans>Loading your machines…</Trans>
					) : Agents.error ? (
						<Trans>Konvo isn't answering right now, probably while it updates. Try again in a minute.</Trans>
					) : (
						<Trans>No machines connected yet.</Trans>
					)}
				</p>
			) : (
				<div className={styles.machines}>
					{Agents.machines.map((machine) => (
						<MachineCard key={machine.id} machine={machine} />
					))}
				</div>
			)}
			{/* Staff: who may merge the agents' pull requests from Konvo (hidden for everyone else). */}
			<AgentMergeRightsSettings />
		</section>
	);
});
